import { useCallback, useSyncExternalStore } from "react";
import { saveForm, type Edit } from "../funnel/formEditor";
import type { Form, RawModel } from "../funnel/forms";

/**
 * LES RETOUCHES EN COURS, HORS DU COMPOSANT QUI LES AFFICHE.
 *
 * ⚠️ L'ÉDITEUR SE DÉMONTE SOUS LES DOIGTS DE L'ORGANISATEUR. Il apparaît dans deux
 * endroits — le panneau de droite, et la carte du `SpotEditor` qui s'ouvre au survol
 * d'un bloc et se referme dès qu'on en sort. Dans la carte, un `useState` local
 * signifiait : je change trois largeurs, la souris passe à côté, tout est perdu. Sans
 * message, sans confirmation, et sans que rien laisse deviner qu'il fallait enregistrer
 * avant de bouger.
 *
 * Le brouillon vit donc ICI, indexé par formulaire. Les deux points de montage lisent
 * le même : ce qu'on commence dans la carte se retrouve dans le panneau, et
 * réciproquement.
 *
 * ⚠️ ET IL PORTE SA PROPRE `base`. Une fois le brouillon ouvert, l'écran affiche
 * l'aperçu — donc le cache de requêtes ne contient plus la vérité du serveur. Un
 * éditeur qui se remonterait en la relisant là-bas capturerait son propre aperçu comme
 * référence : les retouches s'appliqueraient une seconde fois, et la garde
 * anti-écrasement comparerait le formulaire à lui-même retouché. La vérité est donc
 * saisie au moment de la PREMIÈRE retouche, et gardée avec elle.
 */
export type Draft = {
  /** Le formulaire tel que le serveur le donnait avant la première retouche. */
  base: Form;
  patches: Map<number, Edit>;
  order: number[];
  removed: Set<number>;
  additions: RawModel[];
  /** Le prochain identifiant provisoire pour une pose ajoutée — décroissant. */
  nextId: number;
  /**
   * ⚠️ L'ÉVÉNEMENT EST GARDÉ AVEC LE BROUILLON, et pas lu au moment d'enregistrer.
   * L'envoi est déclenché par la barre de l'organisateur — un compte à rebours, ou le
   * bouton commun — à un instant où l'éditeur peut très bien être démonté. Il ne reste
   * alors personne pour dire à quel domaine et à quel événement ce formulaire
   * appartient : le brouillon doit le porter.
   */
  domainId: number;
  eventId: number;
  /** Ce que le serveur a répondu au dernier envoi raté, s'il y en a eu un. */
  failure?: string;
};

const drafts = new Map<number, Draft>();
const subscribers = new Set<() => void>();

function subscribe(notify: () => void) {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

/**
 * ⚠️ L'INSTANTANÉ DOIT ÊTRE STABLE. `useSyncExternalStore` compare la référence rendue
 * d'un rendu à l'autre : reconstruire l'objet à chaque lecture ferait boucler React à
 * l'infini. D'où un `Map` dont on REMPLACE l'entrée à chaque modification, jamais qu'on
 * ne mute — c'est le changement de référence qui signale le changement.
 */
export function useDraft(formId: number | undefined) {
  const read = useCallback(
    () => (formId === undefined ? undefined : drafts.get(formId)),
    [formId],
  );
  return useSyncExternalStore(subscribe, read, read);
}

export function writeDraft(formId: number, next: Draft) {
  drafts.set(formId, next);
  /* L'instant de la dernière retouche : rien ne part tant qu'elle est fraîche — `atRest`. */
  touchedAt = Date.now();
  subscribers.forEach((notify) => notify());
}

/** Enregistré, ou abandonné : dans les deux cas il n'y a plus rien en attente. */
export function forgetDraft(formId: number) {
  drafts.delete(formId);
  subscribers.forEach((notify) => notify());
}

/** Le brouillon d'un formulaire encore vierge — ouvert à la première retouche. */
export function freshDraft(
  base: Form,
  domainId: number,
  eventId: number,
): Draft {
  return {
    base,
    patches: new Map(),
    order: base.fields.map((f) => Number(f.id)),
    removed: new Set(),
    additions: [],
    nextId: -1,
    domainId,
    eventId,
  };
}

/**
 * COMBIEN DE FORMULAIRES ATTENDENT — pour que la barre de l'organisateur les compte
 * avec les réglages.
 *
 * ⚠️ UN NOMBRE, PAS UN BOOLÉEN, et pas non plus le `Map` lui-même : `useSyncExternalStore`
 * compare la référence de l'instantané, et rendre le `Map` ferait boucler React dès
 * qu'on y touche. Un nombre est stable tant qu'il ne change pas.
 */
/** Au moins un brouillon porte un échec d'envoi : le badge de la barre doit le dire. */
export function hasFailedDraft(): boolean {
  for (const d of drafts.values()) if (d.failure) return true;
  return false;
}

export function usePendingDrafts(): number {
  return useSyncExternalStore(
    subscribe,
    () => drafts.size,
    () => 0,
  );
}

/**
 * ── RIEN NE PART PENDANT QU'ON ÉCRIT ──
 *
 * ⚠️ UN ENVOI QUI TOMBE AU MILIEU D'UNE RETOUCHE LAISSE LE BROUILLON PÉRIMÉ, et le
 * suivant échoue en accusant un collègue. La route détruit puis réinsère les placements :
 * leurs identifiants changent à CHAQUE écriture. Le brouillon qui a bougé pendant l'envoi
 * est gardé — c'est voulu, sinon la retouche serait jetée sans un mot — mais sa `base`
 * porte encore les anciens identifiants, et la garde de `saveForm` ne reconnaît plus le
 * formulaire. Elle annonce alors toutes les six secondes qu'il « a été modifié ailleurs ».
 * Personne ne l'a modifié : c'est nous, six secondes plus tôt.
 *
 * On n'envoie donc que ce qui a cessé de bouger. Deux secondes : plus long qu'un
 * aller-retour, assez court pour qu'une pause ordinaire libère le travail.
 *
 * ⚠️ SANS PLAFOND À L'ATTENTE, à dessein. Quelqu'un qui retouche sans jamais souffler
 * repousse l'envoi, et c'est ce qu'il faut : son travail reste dans le brouillon,
 * l'avertissement de sortie est armé, et le premier silence l'emporte.
 */
const QUIET = 2_000;

/** L'instant de la dernière écriture, quel que soit le formulaire. */
let touchedAt = 0;

/**
 * ⚠️ ET RIEN NE PART PENDANT QU'ON CRÉE UNE DONNÉE. La modale de création est la seule
 * écriture de l'éditeur qui ne passe pas par le brouillon : elle attend deux
 * allers-retours — créer la question, puis la relire — et POSE ensuite le champ avec ce
 * que le rendu d'alors connaissait. Un envoi qui aboutit dans cet intervalle oublie le
 * brouillon ; la pose le ressuscite aussitôt, périmé, avec tout ce qui venait de partir.
 * Le formulaire repart alors en double sur une base que le serveur ne reconnaît plus —
 * l'échec relevé en ajoutant une donnée à un événement, en mode automatique.
 */
let composers = 0;

/**
 * Retient l'envoi tant qu'on n'a pas relâché. La relâche est RENDUE plutôt que nommée :
 * c'est le nettoyage d'un effet React, donc le verrou tombe avec la modale — fermée,
 * annulée, ou emportée par un démontage.
 */
export function holdWhileComposing(): () => void {
  composers += 1;
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    composers -= 1;
  };
}

/** Une donnée est en train d'être créée : même un vidage de secours doit attendre. */
export const composing = () => composers > 0;

/** Le moment d'envoyer : personne ne compose, et plus rien n'a bougé depuis un moment. */
export const atRest = (now = Date.now()) =>
  !composing() && now - touchedAt >= QUIET;

/**
 * ENVOYER TOUT CE QUI ATTEND — appelé par la barre, pas par l'éditeur.
 *
 * ⚠️ CE N'EST PAS L'ÉDITEUR QUI ENREGISTRE, et c'est tout le point de cette
 * fonction. Le mode d'enregistrement appartient à l'organisateur, pas à un écran :
 * s'il a demandé l'automatique, son formulaire part tout seul comme ses réglages ;
 * s'il a demandé le manuel, il part avec eux, sur le même bouton. Un bouton
 * « Enregistrer le formulaire » à part disait le contraire de ce réglage.
 *
 * ⚠️ UN ÉCHEC GARDE LE BROUILLON. Rien n'est perdu : le message est rangé dessus,
 * l'éditeur l'affiche, et le formulaire reste « en attente » — donc le bouton reste,
 * ce qui est le seul signal disponible quand l'éditeur n'est pas ouvert.
 */
export async function saveDrafts(
  afterEach: (formId: number) => Promise<void> | void,
): Promise<void> {
  for (const [formId, b] of [...drafts]) {
    try {
      await saveForm(b.domainId, b.eventId, b.base, {
        patches: b.patches,
        order: b.order,
        removed: b.removed,
        additions: b.additions,
      });
      /* ⚠️ SEULEMENT SI RIEN N'A BOUGÉ PENDANT L'ENVOI : une largeur retouchée pendant
         les six secondes d'attente aurait été jetée sans un mot. Si le brouillon a
         changé, on le garde ; il partira au tour suivant. */
      if (drafts.get(formId) === b) forgetDraft(formId);
      await afterEach(formId);
    } catch (cause) {
      const current = drafts.get(formId) ?? b;
      writeDraft(formId, {
        ...current,
        failure:
          cause instanceof Error && cause.message
            ? cause.message
            : "Le formulaire n'a pas pu être enregistré.",
      });
    }
  }
}
